import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

export type CustomerSummary = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  activeFlags: { id: string; flagType: string; reason?: string; autoFlagged: boolean; createdAt: string }[];
  totalBookings: number;
  noShowCount: number;
  totalSpent: number;
  lastBookingDate?: string;
  walletBalance: number;
};

export type CustomerListResponse = {
  customers: CustomerSummary[];
  pagination: { page: number; size: number; totalElements: number; totalPages: number };
};

export type CustomerDetail = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  registeredAt?: string;
  totalBookings: number;
  noShowCount: number;
  totalSpent: number;
  walletBalance: number;
  activeFlags: { id: string; flagType: string; reason?: string; autoFlagged: boolean; createdAt: string }[];
  flagHistory: { id: string; flagType: string; reason?: string; autoFlagged: boolean; active: boolean; createdAt: string; resolvedAt?: string; resolvedByName?: string }[];
  recentBookings: { id: string; date: string; sportName: string; unitName?: string; status: string; amount: number }[];
};

export type CustomerFilters = {
  search?: string;
  flagType?: string;
  sortBy?: string;
  page?: number;
  size?: number;
};

function buildParams(filters: CustomerFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.flagType) params.set('flagType', filters.flagType);
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  params.set('page', String(filters.page ?? 0));
  params.set('size', String(filters.size ?? 20));
  return params.toString();
}

export function useAdminCustomers(filters: CustomerFilters) {
  return useQuery({
    queryKey: ['adminCustomers', filters],
    queryFn: () => apiClient<CustomerListResponse>(`/admin/customers?${buildParams(filters)}`),
  });
}

export function useAdminCustomerDetail(userId?: string) {
  return useQuery({
    queryKey: ['adminCustomerDetail', userId],
    queryFn: () => apiClient<CustomerDetail>(`/admin/customers/${userId}`),
    enabled: !!userId,
  });
}
